
import { Component, Vue } from 'vue-property-decorator';
import { UserPackageList } from '@/collections/user_packages';
import { BillingPackageList } from '@/collections/billing_packages';
import { NoteList } from '@/collections/notes';
import {
  StoreModel,
  PurchaseOrderFullLoadDryRunData,
  PurchaseOrderFullLoadRunData,
} from '@/models/store';
import { UserPackageModel } from '@/models/user_package';
import { NoteModel } from '@/models/note';
import { BillingPackageModel } from '@/models/billing_package';
import { FunnelEventModel } from '@/models/funnelEvent';
import StoreNotes from '@/components/StoreNotes.vue';
import LoginRequestDialog from '@/components/LoginRequestDialog.vue';
import ShopifyAccessTokenDialog from '@/components/ShopifyAccessTokenDialog.vue';
import EtlStatusDialog from '@/components/EtlStatusDialog.vue';
import { revealShopifyAccessToken, ShopifyAccessTokenReveal } from '@/models/shopifyAccessToken';
import { buildImpersonationUrl } from '@/utils/appLinks';

@Component({
  components: {
    StoreNotes,
    LoginRequestDialog,
    ShopifyAccessTokenDialog,
    EtlStatusDialog,
  },
})
export default class Store extends Vue {
  public isLoading: boolean = true;
  public store: StoreModel = new StoreModel();
  public userPackages: UserPackageList = new UserPackageList();
  public userPackage: UserPackageModel = new UserPackageModel();
  public billingPackage: BillingPackageModel = new BillingPackageModel();
  public savingUserPackage: boolean = false;
  public billingPackages: BillingPackageList = new BillingPackageList();
  public selectedBillingPackageId: number | null = null;
  public applyingRules: boolean = false;
  public notes: NoteList = new NoteList();
  public dialog: boolean = false;
  public dialogExtendTrial: boolean = false;
  public newNote: NoteModel = new NoteModel();
  public savingNote: boolean = false;
  public loadingLogin: boolean = false;
  public dialogAction: boolean = false;
  public reportBugAction: boolean = false;
  public cleanCacheAction: boolean = false;
  public reloadWebhookAction: boolean = false;
  public dialogActionProgress: boolean = false;
  public dialogText: string = '';
  public dialogActionMessage: string = '';
  public trialDaysLeft: number = 0;
  public extendDays: number = 7;
  public loadingExtendTrial: boolean = false;
  public extendTrialErrorMessage: string = '';
  public extendTrialSuccessMessage: string = '';
  public extendTrialUrl: string = '';
  public testInventoryAction: boolean = false;
  public testInventoryMessage: string = 'Click the button to test inventory now';
  public dialogTestInventory: boolean = false;
  public showLoginRequestDialog: boolean = false;
  public currentAgentId: string = '';
  public currentAgentName: string = '';
  public currentAgentEmail: string = '';
  public currentLoginRequest: any = null;
  public loadingLoginRequest: boolean = false;
  public cancelingRequest: boolean = false;
  public revokingRequest: boolean = false;
  public statusRefreshInterval: any = null;
  public timeRemaining: string = '';
  public loginRequestHistory: any[] = [];
  public loadingHistory: boolean = false;
  public showHistoryDialog: boolean = false;
  public loadingShopifyToken: boolean = false;
  public showShopifyTokenDialog: boolean = false;
  public shopifyTokenData: ShopifyAccessTokenReveal | null = null;
  public dialogFullLoadPO: boolean = false;
  public showEtlStatusDialog: boolean = false;
  public loadingFullLoadDryRun: boolean = false;
  public loadingFullLoadRun: boolean = false;
  public hasDryRun: boolean = false;
  public fullLoadDryRunResult: PurchaseOrderFullLoadDryRunData | null = null;
  public fullLoadRunResult: PurchaseOrderFullLoadRunData | null = null;
  public fullLoadError: string = '';
  private shopifyTokenRequestGeneration: number = 0;
  private fullLoadRequestGeneration: number = 0;
  public shopifyTokenError: string = '';

  public async created() {
    this.isLoading = true;
    this.store.id = Number(this.$route.params.id);
    this.userPackages.merchantId = this.store.id;
    this.newNote.merchantId = this.store.id;
    await this.store.fetch();
    await this.userPackages.fetch();
    for (const item of this.userPackages.items) {
      if (item.status === 'ACTIVE') {
        this.userPackage = item;
        if (item.price > 0) {
          const trialEndAt = new Date((item.trialEndAt) ? item.trialEndAt : '');
          const currentDate = new Date();
          if (trialEndAt < currentDate) {
            this.trialDaysLeft = 0;
          } else {
            const timeDiff = Math.abs(trialEndAt.getTime() - currentDate.getTime());
            this.trialDaysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
          }
        }
        break;
      }
    }
    this.billingPackage.id = this.userPackage.billingPackageId;
    if (this.billingPackage.id) {
      await this.billingPackage.fetch();
    }
    const newRules = [];
    const currentRuleIds = [];
    for (const rule of this.userPackage.rules.items) {
      currentRuleIds.push(rule.id);
    }
    for (const billingRule of this.billingPackage.rules.items) {
      if (!currentRuleIds.includes(billingRule.id)) {
        newRules.push(billingRule);
      }
    }
    for (const rule of newRules) {
      rule.status = 'INACTIVE';
      this.userPackage.rules.add(rule);
    }
    await Promise.all([
      this.notes.fetch(this.store.id),
      this.billingPackages.fetch({ status: 'ACTIVE' }),
    ]);
    this.isLoading = false;
  }

  public async testInventory() {
    this.testInventoryMessage = '';
    this.testInventoryAction = true;
    try {
      const res: any = await this.store.testInvetory();
      if (res && res.test_pass === 'true' || res.test_pass === true) {
        this.testInventoryMessage = 'The inventory is up-to-date';
      } else {
        this.testInventoryMessage = 'The inventory is not up-to-date';
      }
    } catch (e) {
      this.testInventoryMessage = 'Test inventory failed';
    }
    this.testInventoryAction = false;
  }

  public async saveUserPackage() {
    this.savingUserPackage = true;
    await this.userPackage.save();
    this.savingUserPackage = false;
  }

  public get billingPackageOptions() {
    return this.billingPackages.items.map((p) => ({
      id: p.id,
      text: `${p.name} — $${p.price}`,
    }));
  }

  public stringItems(rule: any) {
    const items = Array.isArray(rule.initValue) ? [...rule.initValue] : [];
    if (rule.value !== undefined && rule.value !== null && !items.includes(rule.value)) {
      items.push(rule.value);
    }
    return items;
  }

  public async applyPackageRules() {
    if (!this.selectedBillingPackageId) {
      return;
    }
    const plan = this.billingPackages.items.find((p) => p.id === this.selectedBillingPackageId);
    const planName = plan ? plan.name : '';
    if (!window.confirm(
      `Replace store #${this.store.id}'s rules with the "${planName}" plan rules? ` +
      `Current rules will be backed up.`,
    )) {
      return;
    }
    this.applyingRules = true;
    try {
      await this.userPackage.applyPackageRules(this.selectedBillingPackageId);
    } catch (e) {
      window.alert('Failed to apply package rules');
    }
    this.applyingRules = false;
  }

  public async revertRules() {
    if (!window.confirm(`Revert store #${this.store.id}'s rules to the backup?`)) {
      return;
    }
    this.applyingRules = true;
    try {
      await this.userPackage.revertRules();
    } catch (e) {
      window.alert('Failed to revert rules');
    }
    this.applyingRules = false;
  }

  public async extendTrial() {
    this.loadingExtendTrial = true;
    const res: any = await this.store.extendTrial(this.extendDays);
    if (res.isError) {
      this.extendTrialErrorMessage = res.message;
      this.extendTrialSuccessMessage = '';
      this.extendTrialUrl = '';
    } else {
      this.extendTrialErrorMessage = '';
      this.extendTrialSuccessMessage = res.message;
      this.extendTrialUrl = res.data;
    }
    this.loadingExtendTrial = false;
  }

  public async createNewNote() {
    this.savingNote = true;
    await this.newNote.save();
    this.newNote = new NoteModel({merchantId: this.store.id});
    this.notes = new NoteList();
    await this.notes.fetch(this.store.id);
    this.savingNote = false;
    this.dialog = false;
  }

  public async loginAsMerchant() {
    this.loadingLogin = true;
    const token: string = await this.store.generateLink();
    this.loadingLogin = false;
    window.open(buildImpersonationUrl(token, this.store.name), '_blank');
  }

  public async bugReport() {
    this.reportBugAction = true;
    this.dialogAction = true;
    this.dialogActionProgress = true;
    this.dialogActionMessage = '';
    this.dialogText = 'Report bug merchant';
    const res = await this.store.bugReport();
    this.dialogActionMessage = res.message;
    this.dialogActionProgress = false;
    this.reportBugAction = false;
  }

  public async clearCache() {
    this.cleanCacheAction = true;
    this.dialogAction = true;
    this.dialogActionProgress = true;
    this.dialogActionMessage = '';
    this.dialogText = 'Clean query caches';
    const res = await this.store.clearCache();
    this.dialogActionMessage = res.message;
    this.dialogActionProgress = false;
    this.cleanCacheAction = false;
  }

  public async reloadWebhook() {
    this.reloadWebhookAction = true;
    this.dialogAction = true;
    this.dialogActionProgress = true;
    this.dialogActionMessage = '';
    this.dialogText = 'Reload webhook data';
    const res = await this.store.reloadWebhook();
    this.dialogActionMessage = res.message;
    this.dialogActionProgress = false;
    this.reloadWebhookAction = false;
  }

  public detectLink(text: string) {
    return text.replace(
      /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim,
      '<a href="$1" target="_blank">$1</a>',
    );
  }

  public async confirmPassedDataVerification() {
    try {
      const funnelEvent = new FunnelEventModel({
        name: 'DATA_VERIFICATION_HUMAN_PASSED',
      });
      await funnelEvent.create(this.store.id);
    } catch (error) {
      // skipped
    }
  }

  public onLoginRequestCreated(request: any) {
    // Handle successful login request creation
    // Refresh the current login request display
    this.fetchLatestLoginRequest();
  }

  public async fetchLatestLoginRequest() {
    try {
      this.loadingLoginRequest = true;
      const { LoginRequestList } = await import('@/collections/loginRequests');
      const list = new LoginRequestList();
      // For admin view, we don't need to filter by customer_id
      // Just get any active request for this merchant
      const request = await list.fetchCurrentActive(this.store.id);
      this.currentLoginRequest = request;

      // Update time remaining if active (pending or approved)
      if (request && (request.status === 'pending' || request.status === 'approved') && !request.isExpired) {
        this.updateTimeRemaining();
      }
    } catch (error) {
      console.error('Error fetching login request:', error);
      this.currentLoginRequest = null;
    } finally {
      this.loadingLoginRequest = false;
    }
  }

  public updateTimeRemaining() {
    if (!this.currentLoginRequest || !this.currentLoginRequest.expiresAt) {
      this.timeRemaining = '';
      return;
    }

    const now = new Date();
    const expiresAt = new Date(this.currentLoginRequest.expiresAt);
    const diff = expiresAt.getTime() - now.getTime();

    if (diff <= 0) {
      this.timeRemaining = 'Expired';
      this.currentLoginRequest.isExpired = true;
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    this.timeRemaining = `${hours}h ${minutes}m remaining`;
  }

  public getStatusColor(status: string): string {
    const colors: any = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      revoked: 'grey',
    };
    return colors[status] || 'grey';
  }

  public getStatusLabel(status: string): string {
    const labels: any = {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      revoked: 'Revoked',
    };
    return labels[status] || status;
  }

  public formatDate(dateString: string): string {
    if (!dateString) { return 'N/A'; }
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  public async cancelLoginRequest() {
    if (!confirm('Are you sure you want to cancel this request?')) {
      return;
    }

    try {
      this.cancelingRequest = true;
      const { post } = await import('@/services/http');
      await post(`/login-requests/${this.currentLoginRequest.id}/cancel`, {});

      // Refresh the status
      await this.fetchLatestLoginRequest();
      alert('Request cancelled successfully');
    } catch (error: any) {
      console.error('Error cancelling request:', error);
      alert('Failed to cancel request: ' + (error.response?.data?.error || error.message));
    } finally {
      this.cancelingRequest = false;
    }
  }

  public async revokeLoginRequest() {
    if (!confirm('Are you sure you want to revoke access?')) {
      return;
    }

    try {
      this.revokingRequest = true;
      const { post } = await import('@/services/http');
      await post(`/login-requests/${this.currentLoginRequest.id}/revoke`, {});

      // Refresh the status
      await this.fetchLatestLoginRequest();
      alert('Access revoked successfully');
    } catch (error: any) {
      console.error('Error revoking access:', error);
      alert('Failed to revoke access: ' + (error.response?.data?.error || error.message));
    } finally {
      this.revokingRequest = false;
    }
  }

  public startStatusRefresh() {
    // Refresh status every 30 seconds
    this.statusRefreshInterval = setInterval(() => {
      if (this.currentLoginRequest && this.currentLoginRequest.status === 'pending') {
        this.fetchLatestLoginRequest();
        this.updateTimeRemaining();
      }
    }, 30000);
  }

  public stopStatusRefresh() {
    if (this.statusRefreshInterval) {
      clearInterval(this.statusRefreshInterval);
      this.statusRefreshInterval = null;
    }
  }

  public mounted() {
    // Initialize current agent information from localStorage or store
    // You may need to adjust this based on how Hades stores user info
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      try {
        const parsed = JSON.parse(userInfo);
        this.currentAgentId = parsed.id || '';
        this.currentAgentName = parsed.name || '';
        this.currentAgentEmail = parsed.email || '';
      } catch (e) {
        // Use fallback values
        this.currentAgentId = 'admin';
        this.currentAgentName = 'Admin User';
        this.currentAgentEmail = 'admin@hades.com';
      }
    } else {
      // Fallback values if no user info is available
      this.currentAgentId = 'admin';
      this.currentAgentName = 'Admin User';
      this.currentAgentEmail = 'admin@hades.com';
    }

    // Fetch login request status
    this.fetchLatestLoginRequest();

    // Start auto-refresh
    this.startStatusRefresh();
  }

  public async fetchLoginRequestHistory() {
    try {
      this.loadingHistory = true;
      const { LoginRequestList } = await import('@/collections/loginRequests');
      const list = new LoginRequestList();
      // Fetch all requests for this merchant
      await list.fetchHistory(this.store.id);
      this.loginRequestHistory = list.items;
    } catch (error) {
      console.error('Error fetching login request history:', error);
      this.loginRequestHistory = [];
    } finally {
      this.loadingHistory = false;
    }
  }

  public async showHistory() {
    await this.fetchLoginRequestHistory();
    this.showHistoryDialog = true;
  }

  public async showShopifyToken() {
    const confirmed = window.confirm('This is a production credential. Do not paste it into tickets, chat, logs, or source code. Continue?');
    if (!confirmed) { return; }
    await this.loadShopifyToken();
  }

  public async loadShopifyToken() {
    this.clearShopifyToken();
    const requestGeneration = ++this.shopifyTokenRequestGeneration;
    this.loadingShopifyToken = true;
    this.shopifyTokenError = '';
    try {
      const tokenData = await revealShopifyAccessToken(this.store.id);
      if (requestGeneration !== this.shopifyTokenRequestGeneration) {
        return;
      }
      this.shopifyTokenData = tokenData;
      this.showShopifyTokenDialog = true;
    } catch (error: any) {
      if (requestGeneration !== this.shopifyTokenRequestGeneration) {
        return;
      }
      this.shopifyTokenError = error.response?.data?.message || 'Unable to retrieve Shopify access token.';
      window.alert(this.shopifyTokenError);
    } finally {
      if (requestGeneration === this.shopifyTokenRequestGeneration) {
        this.loadingShopifyToken = false;
      }
    }
  }

  public clearShopifyToken() {
    this.shopifyTokenRequestGeneration++;
    this.showShopifyTokenDialog = false;
    this.shopifyTokenData = null;
  }

  public closeFullLoadDialog() {
    this.fullLoadRequestGeneration++;
    this.dialogFullLoadPO = false;
    this.hasDryRun = false;
    this.fullLoadDryRunResult = null;
    this.fullLoadRunResult = null;
    this.fullLoadError = '';
    this.loadingFullLoadDryRun = false;
    this.loadingFullLoadRun = false;
  }

  public async runFullLoadDryRun() {
    const requestGeneration = ++this.fullLoadRequestGeneration;
    this.loadingFullLoadDryRun = true;
    this.fullLoadError = '';
    try {
      const res = await this.store.fullLoadPurchaseOrders(true);
      if (requestGeneration !== this.fullLoadRequestGeneration) {
        return;
      }
      this.fullLoadDryRunResult = res.data;
      this.hasDryRun = true;
    } catch (error: any) {
      if (requestGeneration !== this.fullLoadRequestGeneration) {
        return;
      }
      this.fullLoadError = error.response?.data?.message || 'Dry run failed';
      this.fullLoadDryRunResult = null;
      this.hasDryRun = false;
    } finally {
      if (requestGeneration === this.fullLoadRequestGeneration) {
        this.loadingFullLoadDryRun = false;
      }
    }
  }

  public async runFullLoad() {
    if (!window.confirm(
      `This will delete production PO analytics data for store #${this.store.id} and queue a rebuild. Continue?`,
    )) {
      return;
    }
    const requestGeneration = ++this.fullLoadRequestGeneration;
    this.loadingFullLoadRun = true;
    this.fullLoadError = '';
    try {
      const res = await this.store.fullLoadPurchaseOrders(false);
      if (requestGeneration !== this.fullLoadRequestGeneration) {
        return;
      }
      this.fullLoadRunResult = res.data;
    } catch (error: any) {
      if (requestGeneration !== this.fullLoadRequestGeneration) {
        return;
      }
      const data = error.response?.data;
      let message = data?.message || 'Full load failed';
      if (error.response?.status === 409 && data?.data) {
        message += ` (periodId: ${data.data.periodId}, status: ${data.data.status})`;
      }
      this.fullLoadError = message;
      this.fullLoadRunResult = null;
    } finally {
      if (requestGeneration === this.fullLoadRequestGeneration) {
        this.loadingFullLoadRun = false;
      }
    }
  }

  public beforeDestroy() {
    this.clearShopifyToken();
    // Clean up interval
    this.stopStatusRefresh();
  }
}
